import { Grid, Typography, Box, Stack, Button } from "@mui/material";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import "../../assets/styles/common-styles.scss";
import {
  GridSortModel,
  GridColDef,
  GridActionsCellItem,
} from "@mui/x-data-grid-pro";
import { initialPaginationModelValues } from "../../constants/paginationConstants";
import { useCodelist } from "../../hooks/useCodelist";
import { useSnackbar } from "../../hooks/useSnackbar";
import { DataGridComponent } from "../common/DataGridComponent";
import { useParams } from "react-router-dom";
import { debounce } from "lodash";
import { getChipType } from "../../pages/Codelists";
import { AppState, useAppSelector } from "../../store";
import EditIcon from "@mui/icons-material/Edit";
import CodelistValuesDialog from "./CodelistValuesDialog";
import AddIcon from "@mui/icons-material/Add";
import PermissionCheck from "../common/PermissionCheck/PermissionCheck";
import { usePermissionWrapper } from "../../hooks/usePermissionWrapper";

const CodelistValues: React.FunctionComponent<any> = () => {
  const [rowData, setRowData] = useState([]);
  const { handleError } = useSnackbar();
  const [rowsCount, setRowsCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState(
    initialPaginationModelValues
  );
  const [filterObj, setFilterObject] = useState("");
  const { checkPermission } = usePermissionWrapper();
  const {
    getCodelistValues,
    codelistValuesData,
    codelistValuesIsLoading,
    codelistValuesIsError,
    codelistValuesError,
  } = useCodelist();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "code_list_value_code", sort: "asc" },
  ]);
  const params = useParams();
  const codelistDetails = useAppSelector(
    (state: AppState) => state.commonSlice.codelistDetails
  );
  const [isEdit, setIsEdit] = useState(false);
  const [openCodelistDialog, setOpenCodelistDialog] = useState(false);
  const [codeDetails, setCodeDetails] = useState({});
  const handleSortModelChange = useCallback(
    debounce((model: GridSortModel) => {
      setSortModel(
        model ? model : [{ field: "code_list_value_code", sort: "asc" }]
      );
    }, 300),
    []
  );
  const onAddClick = () => {
    setOpenCodelistDialog(true);
  };
  const onEditClick = (row: any) => {
    setCodeDetails(row);
    setIsEdit(true);
    setOpenCodelistDialog(true);
  };
  const getAllCodelistValuesFunc = (
    from: number,
    size: number,
    filterObj?: any
  ) => {
    getCodelistValues({
      code_list_code: params?.code_list_code,
      from,
      size,
      value: filterObj && filterObj?.quickFilter ? filterObj?.quickFilter : "",
      sort_by: sortModel?.[0]?.field,
      sort_order: sortModel?.[0]?.sort,
      data: filterObj && filterObj?.advanceFilter,
    });
  };

  useEffect(() => {
    getCodelistValues({
      code_list_code: params?.code_list_code,
      is_audit: true,
    });
  }, []);
  useEffect(() => {
    if (sortModel.length === 0) {
      setSortModel([{ field: "code_list_value_code", sort: "asc" }]);
    }
  }, [sortModel]);
  useEffect(() => {
    if (paginationModel?.pageSize) {
      const { page, pageSize } = paginationModel;
      if (page * pageSize > rowsCount - 1) {
        getAllCodelistValuesFunc(0, pageSize, filterObj);
      } else {
        getAllCodelistValuesFunc(page * pageSize, pageSize, filterObj);
      }
    }
  }, [paginationModel, filterObj]);

  useEffect(() => {
    if (codelistValuesData && codelistValuesData?.count > 0) {
      const data = codelistValuesData?.result?.map((ele: any) => {
        return {
          ...ele,
          id: ele.code_list_value_code,
        };
      });
      setRowsCount(codelistValuesData?.count);
      setRowData(data);
    } else if (codelistValuesData?.count == 0) {
      setRowsCount(0);
      setRowData([]);
    }
  }, [codelistValuesData]);

  useEffect(() => {
    if (codelistValuesIsError) {
      setRowData([]);
      setRowsCount(0);
      const error: any = codelistValuesError;
      handleError(error);
    }
  }, [codelistValuesIsError]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "code_list_value_code",
        headerName: "Value Code",
        minWidth: 100,
        flex: 1,
      },
      {
        field: "code_list_value",
        headerName: "Value",
        minWidth: 100,
        flex: 1,
      },
      {
        field: "code_value_definition",
        headerName: "Code Definition",
        minWidth: 100,
        flex: 1,
      },
      {
        field: "code_submission_value",
        headerName: "Code Submission Value",
        minWidth: 100,
        flex: 1,
      },
      {
        field: "actions",
        width: 90,
        align: "center",
        type: "actions",
        headerName: "Actions",
        sortable: false,
        getActions: (params) => [
          codelistDetails?.category === "Modifiable" ? (
            <GridActionsCellItem
              key={params.row.id}
              icon={<EditIcon />}
              label="Edit"
              onClick={() => onEditClick(params.row)}
              showInMenu
              disabled={!checkPermission("EDIT_CODELIST_VALUE")}
            />
          ) : (
            <GridActionsCellItem
              key={params.row.id}
              label="No actions"
              showInMenu
              disabled
            />
          ),
        ],
      },
    ],
    []
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs>
          <Stack direction="row" spacing={2} className="dFlex alignItemsCenter">
            <Typography variant="h4" gutterBottom>
              Codelist - {`${codelistDetails?.code_list_code}`}
            </Typography>
            <Box sx={{ mt: 5 }}>{getChipType(codelistDetails?.category)}</Box>
          </Stack>
        </Grid>
        {codelistDetails?.category === "Extensible" && (
          <PermissionCheck permission="ADD_CODELIST_VALUE">
            <Grid item justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                endIcon={<AddIcon />}
                id="add_codelist_value_code"
                onClick={() => onAddClick()}
                aria-label={`Click to Add Value Code`}
              >
                Add Value Code
              </Button>
            </Grid>
          </PermissionCheck>
        )}
      </Grid>

      <Box
        className="dataGridWrapBox"
        sx={{ width: "100%", height: "calc(100vh - 160px)", mt: 2 }}
      >
        <DataGridComponent
          columns={columns}
          rows={rowData ?? []}
          showQuickFilter
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={codelistValuesIsLoading}
          rowsCount={rowsCount}
          filterMode="server"
          filterValues={(data: any) => setFilterObject(data)}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          disableRowSelectionOnClick
          exportFileName="Audit_Trail"
        />
      </Box>
      {openCodelistDialog && (
        <CodelistValuesDialog
          openCodelistDialog={openCodelistDialog}
          setOpenCodelistDialog={setOpenCodelistDialog}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          codeDetails={codeDetails}
          setCodeDetails={setCodeDetails}
          onSuccess={() => {
            getAllCodelistValuesFunc(0, paginationModel?.pageSize, filterObj);
          }}
        />
      )}
    </>
  );
};

export default memo(CodelistValues);
